import { Navigate, createRoute } from "@tanstack/react-router";
import { useAuth } from "@clerk/clerk-react";
import { useOrganizerClubs } from "@/lib/clubs/queries";
import { ClubsEmptyState } from "@/components/ClubsEmptyState";
import { MultiClubHome } from "@/components/home/MultiClubHome";
import { WelcomeHub } from "@/components/welcome-hub/WelcomeHub";
import { MarketingLanding } from "@/components/marketing/MarketingLanding";
import { useAccessibleSurfaces } from "@/lib/auth/useAccessibleSurfaces";
import { rootRoute } from "./root";

export function HomeRoute() {
  const { isLoaded, isSignedIn } = useAuth();
  const clubsQuery = useOrganizerClubs();
  const {
    isLoading: rolesLoading,
    surfaces,
    isStaff,
    isFacilitator,
  } = useAccessibleSurfaces();

  if (!isLoaded) return <Loading />;
  if (!isSignedIn) return <MarketingLanding />;

  if (rolesLoading || clubsQuery.isLoading) return <Loading />;
  if (clubsQuery.isError || !clubsQuery.data) {
    return (
      <ClubsEmptyState
        reason="error"
        error={clubsQuery.error}
        onRetry={() => void clubsQuery.refetch()}
      />
    );
  }

  const clubs = clubsQuery.data;

  // Hybrid (multi-role) — show the welcome hub.
  if (surfaces.length >= 2) {
    return <WelcomeHub accessibleSurfaces={surfaces} clubs={clubs} />;
  }

  // Single staff-only role with no clubs — drop straight into the operator
  // console.
  if (isStaff && clubs.length === 0 && !isFacilitator) {
    return <Navigate to="/staff" replace />;
  }

  // Single facilitator-only role with no clubs — drop into the facilitator
  // dashboard.
  if (isFacilitator && clubs.length === 0 && !isStaff) {
    return <Navigate to="/facilitate" replace />;
  }

  // Organizer-only branch.
  if (clubs.length === 0) {
    return <ClubsEmptyState reason="not-organizer" />;
  }

  if (clubs.length === 1) {
    return <Navigate to="/clubs/$id" params={{ id: clubs[0].id }} replace />;
  }

  return <MultiClubHome clubs={clubs} />;
}

function Loading() {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{ padding: 96, textAlign: "center" }}
    >
      Loading…
    </div>
  );
}

export const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: HomeRoute,
});
